import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FETCH_RECEIPTS_CSV_REPORT_V2, showMessage } from '~/actions';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Column, ConfirmPopup, InputSelect, Label } from '~/commonComponents';
import { paymentHistoryFilterOptionsLabels } from '~/constants';
import { useIsSuccess } from '~/hooks';
import { ReceiptTransactionDateEnum } from '~/utils/Enum';
import { useDownloadReportV2 } from './hooks';
import { NumberUtils } from '~/utils/NumberUtils';

interface IProps {
  onClosePopup: () => void;
}

export const DownloadReportPopup = ({ onClosePopup }: IProps) => {
  const dispatch = useDispatch();

  const [formDateRange, setFormDateRange] = useState<string>('');

  const { isLoading, onDownloadReport } = useDownloadReportV2();
  const { isSuccess, clearSuccess } = useIsSuccess([FETCH_RECEIPTS_CSV_REPORT_V2]);

  useEffect(() => {
    if (!isSuccess) return;

    dispatch(
      showMessage({
        popupProps: {
          rightButtonAction: () => {
            onClosePopup();
            clearSuccess();
          },
          rightButtonText: 'Done',
        },
        title: 'Report Downloaded Successfully!',
        message:
          // eslint-disable-next-line max-len
          `Your transaction report was downloaded successfully. If you need further assistance, please text us at ${NumberUtils.getCSPhone().formatted}.`,
        isSuccess: true,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  if (isSuccess) {
    return null;
  }

  return (
    // @ts-expect-error ConfirmPopup component still written in JavaScript
    <ConfirmPopup
      dataCy={"download-report-popup"}
      title="Download Report"
      titleStyle={{
        fontSize: 18,
      }}
      leftButtonAction={onClosePopup}
      leftButtonDisabled={isLoading}
      leftButtonText="Close"
      leftButtonDataCy="close-download-popup-button"
      rightButtonAction={() => onDownloadReport(formDateRange as ReceiptTransactionDateEnum)}
      rightButtonDisabled={isLoading || !formDateRange}
      rightButtonLoading={isLoading}
      rightButtonText="Download"
      rightButtonDataCy="download-report-button"
    >
      <Column alignItems="center">
        <Typography as="span" textAlign="center" color={colors.neutral[500]}>
          Before proceeding with the download, please select a<br />
          transaction date range from the dropdown below.
        </Typography>

        <Column marginTop={32} marginBottom={38} width={385}>
          <Label text="Transaction Date Range:" required />
          <InputSelect
            dataCy='time-range-option'
            value={formDateRange}
            setValue={setFormDateRange}
            isSelectVisible={false}
            options={Object.entries(paymentHistoryFilterOptionsLabels).map(([key, name]) => ({
              value: key,
              title: name,
            }))}
          />
        </Column>
      </Column>
    </ConfirmPopup>
  );
};
